import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

import { PlacementErrorType } from '@frontend/sports/types/betslip';
import { Store } from '@ngrx/store';
import { EpcotConfigService, EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';
import { Observable, Subscription, combineLatest, distinctUntilChanged, map, switchMap } from 'rxjs';

import { selectIsLinearBetslip } from '../../../../base/store/selectors';
import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipPick } from '../../../../core/picks/betslip-pick';
import { ResultError } from '../../../validation/errors/result/result-error';
import {
    selectAllPickErrorsForPickFactory,
    selectCurrentPickErrorsForPickFactory,
    selectLinearGlobalIndiactorErrorsForPickFactory,
    selectTypePickErrorsForPickFactory,
} from '../../../validation/selectors';

@Component({
    selector: 'bs-digital-combo-prevention-indicator',
    template: ` <bs-digital-combo-prevention-icon
        *ngIf="hasComboPreventionError"
        [isLinear]="isLinear"
        [isEpcotEnabled]="isEpcotEnabled"></bs-digital-combo-prevention-icon>`,
    styleUrls: ['./pick-combo-prevention-indicator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export default class PickComboPreventionIndicatorComponent implements OnChanges, OnDestroy {
    @Input() pick: BetslipPick;
    @Input() betslipType?: BetslipType;
    @Input() isLinearGlobalIndicator?: boolean;

    @HostBinding('class') get className(): string {
        return this.isLinear ? 'betslip-digital-pick__combo-prevention-indicator-linear' : 'betslip-digital-pick__combo-prevention-indicator';
    }

    hasComboPreventionError = false;
    subscription: Subscription | undefined;
    isEpcotEnabled = this.epcotConfigService.isEnabled(EpcotModule.Betslip);

    isLinear = false;

    private readonly isLinear$: Observable<boolean>;

    constructor(
        private store: Store<IBetslipRootState>,
        private changeDetector: ChangeDetectorRef,
        private epcotConfigService: EpcotConfigService,
    ) {
        this.isLinear$ = this.store.select(selectIsLinearBetslip);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const { pick } = changes;

        if (pick && this.pick) {
            if (this.subscription) {
                this.subscription.unsubscribe();
            }

            const isLinearGlobalIndicator = this.isLinearGlobalIndicator;

            const betslipType = this.betslipType;

            const errors$ = this.isLinear$.pipe(switchMap((isLinear) => this.getErrors(isLinear, betslipType, isLinearGlobalIndicator)));

            this.subscription = combineLatest([errors$, this.isLinear$])
                .pipe(
                    map(([errors, isLinear]) => [
                        errors.some((e) => e.type === PlacementErrorType.ComboPrevention || e.type === PlacementErrorType.MinimumCombo),
                        isLinear,
                    ]),
                    distinctUntilChanged(),
                )
                .subscribe(([hasComboPreventionError, isLinear]) => {
                    this.hasComboPreventionError = hasComboPreventionError;
                    this.isLinear = isLinear && betslipType !== BetslipType.EditBet;
                    this.changeDetector.markForCheck();
                });
        }
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    private getErrors(isLinear: boolean, betslipType?: BetslipType, isLinearGlobalIndicator?: boolean): Observable<ResultError[]> {
        if (!isLinear) {
            return this.store.select(selectCurrentPickErrorsForPickFactory(this.pick.id));
        }

        if (betslipType) {
            return this.store.select(selectTypePickErrorsForPickFactory(this.pick.id, betslipType));
        }

        if (isLinearGlobalIndicator) {
            return this.store.select(selectLinearGlobalIndiactorErrorsForPickFactory(this.pick.id));
        }

        return this.store.select(selectAllPickErrorsForPickFactory(this.pick.id));
    }
}
